import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import Loader from "../components/common/custom-component/Loader";
import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import CustomPagination from "../components/common/CustomPagination";
import DataContainer from "../components/common/DataContainer/DataContainer";

import { CalendarToday as CalendarIcon } from "@mui/icons-material";

import { Alert, Box, MenuItem, TextField, Typography, Divider } from "@mui/material";

import { useTranslation } from "react-i18next";
import { useToast } from "../components/common/custom-component/ToastProvider";

import { fetchUnassignedTasksRequest } from "../store/slices/workflows/unassignedTasksSlice";
import { createPendingTasksRequest } from "../dto/workflowDto";
import SearchFilterBar from "../components/common/SearchFilterBar";
import { useTableColumns } from "../hooks/useTableColumns";
import { useWorkflowAccess } from "../hooks/useWorkflowAccess";
import { claimUnassignedTaskThunk } from "store/thunks/workflowThunks";
import { unassignedTaskReducer, initialState } from "../reducers/unassignedTaskReducer";
import { logger } from "helpers/logger";
import { getErrorMessage } from "helpers/errorUtils";
import { toDDMMYYYY } from "helpers/dateUtils";

// sessionStorage key for the page number to return to after running a claimed task.
const SAVED_PAGE_KEY = "unassignedTasksPageIndex";

function UnassignedTaskPage() {
	const { error: ToastError } = useToast();
	const [t] = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();

	// Own slice (not pendingTasks): see unassignedTasksSlice.
	const { data: UnassignedTaskData, loading, error } = useSelector((state) => ({
		data: state.unassignedTasks?.data?.data,
		loading: state.unassignedTasks?.loading,
		error: state.unassignedTasks?.error,
	}));
	const selectedCompany = useSelector((state) => state.selectedCompany.data?.companyData);

	// Table state (page, size, search, sort). The init function restores the page saved before
	// leaving to run a claimed task, then clears it right away so a later normal visit starts at
	// page 1. Page-specific key: other pages save their own.
	const [state, dispatchState] = useReducer(unassignedTaskReducer, initialState, (init) => {
		const savedPage = sessionStorage.getItem(SAVED_PAGE_KEY);
		sessionStorage.removeItem(SAVED_PAGE_KEY);
		return savedPage
			? { ...init, table: { ...init.table, currentPage: parseInt(savedPage, 10) || 1 } }
			: init;
	});
	const recordsPerPage = state.table.recordsPerPage || 10;
	const currentPage = state.table.currentPage;
	const searchParams = state.table.searchParams;

	const totalCount = UnassignedTaskData?.totalCount;

	const totalPages = Math.max(1, Math.ceil((totalCount || 0) / recordsPerPage));

	// #region FILTERS & SORT
	// Handlers only change the table state; the fetch effect reloads whenever it changes. Search
	// is done by the backend (search endpoint), not by filtering the loaded page.

	const applyFilters = () => {
		dispatchState({
			type: "SET_SEARCH_PARAMS",
			payload: {
				searchTerm: state.table.searchInput?.trim() || "",
				startDate: toDDMMYYYY(state.table.startDateInput),
				endDate: toDDMMYYYY(state.table.endDateInput),
			},
		});
	};

	const clearFilters = () => {
		dispatchState({ type: "CLEAR_FILTERS" });
	};

	// DataTable calls onSort(columnKey) only. Same column flips asc/desc; a new column starts asc.
	// byLabel comes from the column config (sortByLabel), so no column names live in code.
	const handleSort = (column) => {
		if (!column) return;
		const direction = column === state.table.sortField && state.table.sortDirection === "asc" ? "desc" : "asc";
		const byLabel = columns.find((c) => c.key === column)?.sortByLabel === true;
		dispatchState({ type: "SET_SORT", payload: { field: column, direction, byLabel } });
	};

	const handleChangeRecordsPerPage = (e) => {
		dispatchState({ type: "SET_RECORDS_PER_PAGE", payload: Number(e.target.value) || 10 });
	};

	const handlePageChange = (event, value) => {
		dispatchState({ type: "SET_PAGE", payload: value });
	};

	// #endregion


	// #region FETCH UNASSIGNEDTASKS

	// Fetches from the current page/size/search/sort state. The backend takes the user from the
	// token, and limits rows to tasks offered to them (candidate user, or one of their roles in the
	// selected company) in workflows where they may claim.
	const fetchUnassignedTasks = useCallback(() => {
		const request = createPendingTasksRequest({
			pageNumber: currentPage,
			recordsByPage: recordsPerPage,
			searchParam: searchParams.searchTerm,
			startDate: searchParams.startDate,
			endDate: searchParams.endDate,
			sortField: state.table.sortField,
			sortDirection: state.table.sortDirection,
		});
		// Search text and label sorts need the translated labels, so they go to the search endpoint.
		const needsLabels = !!request.searchParam || state.table.sortByLabel;

		dispatch(fetchUnassignedTasksRequest({ request, needsLabels }));
	}, [dispatch, currentPage, recordsPerPage, searchParams, state.table.sortField, state.table.sortDirection, state.table.sortByLabel]);

	// location.key: refetch when the user navigates to this page again (even from the same URL).
	useEffect(() => {
		fetchUnassignedTasks();
	}, [fetchUnassignedTasks, location.key, selectedCompany]);

	// Based on whether a response is in the store, not on how many rows it has: an empty list is
	// still a loaded answer ("no tasks"). Never loaded (or cleared by a company switch) -> full
	// loader; loaded before, even with 0 tasks -> keep showing it with a small refresh spinner.
	const hasLoaded = UnassignedTaskData != null;
	const isFirstLoad = loading && !hasLoaded;
	const isBackgroundRefreshing = loading && hasLoaded;

	// #endregion

	// #region CLAIM TASK
	// Claims the task for the logged-in user and opens its form right away, the same way Pending
	// Tasks' Execute does. The backend checks everything before claiming (company, nobody has the
	// task, claim_task, offered to the user, the form), so only the task id is sent; the user comes
	// from the token.
	const handleClaim = useCallback(
		async (row) => {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

			try {
				const result = await dispatch(claimUnassignedTaskThunk({ taskId: row.task_id })).unwrap();

				if (result?.data) {
					sessionStorage.setItem(SAVED_PAGE_KEY, String(currentPage));
					navigate("/formRenderer", {
						state: {
							...result.data,
							title: t("Execute Task"),
						},
					});
				} else {
					ToastError(result?.message || t("Failed to claim the task"));
				}
			} catch (error) {
				logger.error("Error claiming task:", error);
				ToastError(getErrorMessage(error, t("Failed to claim the task")));
				// E.g. someone else claimed it first: reload so the row disappears.
				fetchUnassignedTasks();
			} finally {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}
		},
		[dispatch, navigate, t, ToastError, currentPage, fetchUnassignedTasks]
	);

	// #endregion

	// #region UNASSIGNEDTASKS COLUMNS

	const actions = useMemo(() => ({
		onClaim: handleClaim,
	}), [handleClaim]);

	// The Claim button's rule in tbl_table_column_configs uses "hasWorkflowPermission", which calls
	// canUse(permissionCode, row.process_definition_key).
	const { canUse } = useWorkflowAccess();
	const context = useMemo(() => ({ canUse }), [canUse]);

	const { columns, isLoading: columnsLoading } = useTableColumns('UnassignedTasks', { actions, context });

	// #endregion

	// #region RETURN

	// No rows to show and the fetch failed (also when nothing was ever loaded).
	if (error && !UnassignedTaskData?.tasks?.length) {
		return <Alert severity="error">{error}</Alert>;
	}

	return (
		<Box sx={{ p: 3 }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
				<Typography variant="h3" color="primary">
					{t("Unassigned Tasks")}
				</Typography>

				{isBackgroundRefreshing && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Loader variant="circular" message="Loading..." size="small" />
					</Box>
				)}
			</Box>

			<Typography variant="body1" color="text.secondary" paragraph>
				{t("View and claim tasks that haven't been assigned to anyone yet.")}
			</Typography>

			<Divider sx={{ my: 3 }} />

			{/* Filters */}
			<Box
				sx={{
					my: 2,
					p: 2,
					borderRadius: 2,
					bgcolor: "rgba(29,131,84,0.04)",
					display: "flex",
					flexWrap: "wrap",
					alignItems: "center",
					gap: 2,
				}}
			>
				{/* Search */}
				<Box sx={{ flex: 1, minWidth: 260 }}>
					<SearchFilterBar
						/* Backend search & filtering */
						mode="backend"
						/* Search input */
						searchValue={state.table.searchInput}
						onSearchChange={(v) => dispatchState({ type: "SET_SEARCH_INPUT", payload: v })}
						searchPlaceholder={t("Search Unassigned Task...")}
						searchBarProps={{
							sx: {
								bgcolor: "white",
								borderRadius: 1,
								"& .MuiOutlinedInput-root": { borderRadius: 1 },
							},
						}}
						/* Date range filters */
						showDateRange={true}
						startDateLabel={t("Start date")}
						endDateLabel={t("End date")}
						startDateValue={state.table.startDateInput}
						endDateValue={state.table.endDateInput}
						onStartDateChange={(v) => dispatchState({ type: "SET_START_DATE_INPUT", payload: v })}
						onEndDateChange={(v) => dispatchState({ type: "SET_END_DATE_INPUT", payload: v })}
						dateInputSx={{
							minWidth: 170,
							"& .MuiOutlinedInput-root": {
								"& fieldset": { borderColor: "primary.main" },
								"&:hover fieldset": { borderColor: "primary.main" },
								"&.Mui-focused fieldset": { borderColor: "primary.main" },
							},
						}}
						/* Apply / Clear actions */
						showActions={true}
						applyText={t("Apply")}
						clearText={t("Clear")}
						onApply={applyFilters}
						onClear={clearFilters}
						applyButtonProps={{
							sx: { display: "flex", alignItems: "center", gap: 1, textTransform: "uppercase" },
						}}
						clearButtonProps={{
							sx: { display: "flex", alignItems: "center", gap: 1, textTransform: "uppercase" },
						}}
						/* Disable unused UI elements */
						showFilterButton={false}
						showSearchButton={false}
					/>
				</Box>
			</Box>

			{/* First load: header and filters stay; only the results area (total, table, paging) waits. */}
			{isFirstLoad || columnsLoading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
					<Loader variant="dots" message="Loading..." />
				</Box>
			) : (
			<>
			<Typography variant="body1">
				{t("Total Unassigned Tasks")}: {totalCount || 0}
			</Typography>

			{!UnassignedTaskData?.tasks?.length ? (
				<DataContainer data={[]} emptyStateIcon={CalendarIcon} emptyStateTitle={t("No Unassigned Tasks yet")} emptyStateSubtitle={t("Wait for someone to assign a task for your group")} />
			) : (
				<Box sx={{ mt: 2 }}>
					<DataContainer
						data={UnassignedTaskData.tasks}
						columns={columns}
						onSort={handleSort}
						sortConfig={{ key: state.table.sortField, direction: state.table.sortDirection }}
						showRowNumbers
						maxHeight={600}
						stickyHeader
					/>
				</Box>
			)}

			<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
				<TextField
					select
					size="small"
					label={t("Per page")}
					value={recordsPerPage}
					onChange={handleChangeRecordsPerPage}
					sx={{
						minWidth: 120,
						"& .MuiOutlinedInput-root": {
							"& fieldset": { borderColor: "primary.main" },
							"&:hover fieldset": { borderColor: "primary.main" },
							"&.Mui-focused fieldset": { borderColor: "primary.main" },
						},
					}}
				>
					{[5, 10, 15].map((n) => (
						<MenuItem key={n} value={n}>
							{n}
						</MenuItem>
					))}
				</TextField>

				<CustomPagination
					currentPage={currentPage}
					totalPages={totalPages}
					onPageChange={handlePageChange}
					color="primary"
					showFirstButton
					showLastButton
					sx={{ mt: 2 }}
				/>
			</Box>
			</>
			)}

			<LoadingBackdrop open={state.loading.isLoadingBackdrop} message={t("Loading data, please wait...")} variant="bars" color="greenGradient" />
		</Box>
	);

	// #endregion
}

export default UnassignedTaskPage;
